import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent, Box, Typography, Button, Stack, Divider, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { Cookie, WarningAmberRounded, Refresh, SettingsOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { checkThirdPartyCookieSupport } from 'utils/cookieChecker';

// Modern styling matching Acemoney banking design tokens
const BLUE_PRIMARY = '#1976d2';
const BLUE_DARK = '#1565c0';
const BLUE_ACCENT = '#42a5f5';
const GRADIENT_PRIMARY = 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)';
const GRADIENT_LIGHT = 'linear-gradient(135deg, #e3f2fd 0%, #f5f9ff 100%)';

const ProfessionalDialog = styled(Dialog)(({ theme }) => ({
    zIndex: theme.zIndex.tooltip + 10,
    '& .MuiDialog-paper': {
        background: '#ffffff',
        borderRadius: '16px',
        maxWidth: '560px',
        width: '100%',
        boxShadow: '0px 20px 60px rgba(25, 118, 210, 0.18), 0px 0px 1px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '5px',
            background: GRADIENT_PRIMARY
        }
    },
    '& .MuiBackdrop-root': {
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(6px)'
    }
}));

const IconContainer = styled(Box)(() => ({
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    background: GRADIENT_LIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '12px auto 20px',
    border: `3px solid ${BLUE_ACCENT}`,
    position: 'relative',
    boxShadow: '0 8px 24px rgba(25, 118, 210, 0.2)',
    '& svg': {
        fontSize: '44px',
        color: BLUE_PRIMARY,
        position: 'relative',
        zIndex: 1,
        filter: 'drop-shadow(0 2px 4px rgba(25, 118, 210, 0.2))'
    }
}));

const WarningBox = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    padding: '16px 20px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%)',
    border: '1.5px solid #ffe082',
    textAlign: 'left',
    marginTop: '16px',
    marginBottom: '20px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(255, 193, 7, 0.12)',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        background: 'linear-gradient(180deg, #ff9800 0%, #ffc107 100%)'
    }
}));

const GuidanceCard = styled(Box)(() => ({
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    background: '#f8fafc',
    padding: '16px 20px',
    textAlign: 'left',
    marginBottom: '20px'
}));

/**
 * Mandatory Cookie Permission Dialog Component.
 * Detects if third-party cookies or cookies are blocked and provides browser setup guidance.
 */
export default function CookiePermissionDialog({ forceOpen = false, onPermissionGranted }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isBlocked, setIsBlocked] = useState(false);
    const [checking, setChecking] = useState(true);

    const verifyCookiePermission = useCallback(async () => {
        setChecking(true);
        try {
            const allowed = await checkThirdPartyCookieSupport();
            const blockedState = forceOpen ? true : !allowed;
            setIsBlocked(blockedState);
            if (allowed && onPermissionGranted) {
                onPermissionGranted();
            }
        } catch (error) {
            console.error('Failed to verify cookie permissions:', error);
            setIsBlocked(true);
        } finally {
            setChecking(false);
        }
    }, [forceOpen, onPermissionGranted]);

    useEffect(() => {
        verifyCookiePermission();

        // Auto re-check when user switches back to this tab (e.g. after updating browser settings)
        const handleWindowFocus = () => {
            verifyCookiePermission();
        };

        window.addEventListener('focus', handleWindowFocus);
        return () => {
            window.removeEventListener('focus', handleWindowFocus);
        };
    }, [verifyCookiePermission]);

    if (!isBlocked && !checking && !forceOpen) {
        return null;
    }

    return (
        <ProfessionalDialog
            open={isBlocked || forceOpen}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                // Mandatory modal - prevent backdrop click or escape key from closing
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            PaperProps={{
                sx: {
                    m: isMobile ? 0 : 2,
                    borderRadius: isMobile ? 0 : '16px'
                }
            }}
        >
            <DialogContent sx={{ p: isMobile ? 2.5 : 3.5, textAlign: 'center' }}>
                <IconContainer>
                    <Cookie />
                </IconContainer>

                <Typography
                    variant="h5"
                    sx={{
                        background: GRADIENT_PRIMARY,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontWeight: 700,
                        mb: 1,
                        fontSize: isMobile ? '22px' : '26px',
                        letterSpacing: '-0.5px'
                    }}
                >
                    Cookie Permission Required
                </Typography>

                <Typography variant="body2" sx={{ color: '#546e7a', fontWeight: 500, mb: 1, px: isMobile ? 0 : 2 }}>
                    To ensure secure session authentication and uninterrupted service access, Acemoney requires cookies to be enabled.
                </Typography>

                <WarningBox>
                    <WarningAmberRounded
                        sx={{
                            color: '#f57c00',
                            mt: 0.3,
                            fontSize: '26px',
                            flexShrink: 0
                        }}
                    />
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="subtitle2" sx={{ color: '#b78103', fontWeight: 700, mb: 0.5, fontSize: '14px' }}>
                            Third-Party Cookies are Blocked
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6d4c41', lineHeight: 1.6, fontSize: '13px' }}>
                            Your browser settings currently restrict third-party cookies or cross-site session access. Please follow the
                            instructions below to enable cookies in your browser settings.
                        </Typography>
                    </Box>
                </WarningBox>

                <GuidanceCard>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                        <SettingsOutlined sx={{ color: BLUE_PRIMARY, fontSize: '20px' }} />
                        <Typography variant="subtitle2" sx={{ color: BLUE_DARK, fontWeight: 700, fontSize: '14px' }}>
                            How to enable cookies in your browser settings:
                        </Typography>
                    </Stack>

                    <Divider sx={{ mb: 1.5 }} />

                    <Stack spacing={1.5}>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                Google Chrome:
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ color: '#475569', lineHeight: 1.5 }}>
                                Click lock/eye icon near URL bar &rarr; Site settings &rarr; Allow Third-party cookies (or turn off
                                &quot;Block third-party cookies&quot; in Settings &gt; Privacy & Security).
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                Safari (macOS / iOS):
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ color: '#475569', lineHeight: 1.5 }}>
                                Go to Settings &gt; Safari &gt; Privacy & Security &rarr; Turn off &quot;Prevent Cross-Site Tracking&quot;
                                and &quot;Block All Cookies&quot;.
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b' }}>
                                Microsoft Edge:
                            </Typography>
                            <Typography variant="caption" display="block" sx={{ color: '#475569', lineHeight: 1.5 }}>
                                Settings &gt; Cookies and site permissions &gt; Manage and delete cookies &rarr; Turn off &quot;Block
                                third-party cookies&quot;.
                            </Typography>
                        </Box>
                    </Stack>
                </GuidanceCard>

                <Button
                    variant="contained"
                    onClick={verifyCookiePermission}
                    disabled={checking}
                    startIcon={checking ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
                    sx={{
                        width: '100%',
                        py: 1.4,
                        borderRadius: '12px',
                        fontWeight: 600,
                        textTransform: 'none',
                        fontSize: '15px',
                        background: GRADIENT_PRIMARY,
                        color: '#fff',
                        boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)'
                        }
                    }}
                >
                    {checking ? 'Checking Status...' : 'I Have Enabled Cookies - Re-check Permission'}
                </Button>
            </DialogContent>
        </ProfessionalDialog>
    );
}

CookiePermissionDialog.propTypes = {
    forceOpen: PropTypes.bool,
    onPermissionGranted: PropTypes.func
};
